import { http, HttpHandler } from 'msw';
import type { HttpRequestHandler } from 'msw/core/http';

interface IResponseOptions {
  statusCode?: number;
  response?: unknown;
}

/**
 * Creates an MSW handler for the given endpoint and options.
 *
 * NOTE: We log API errors to the console by default, which will cause tests to fail. If using
 * this in tests with an error status code, you can mock the console to avoid the error, e.g.:
 * @example
 * ```ts
 * const consoleErrorSpy = vi.spyOn(console, 'error').mockImplementation(() => {});
 * < code that triggers the API call >
 * consoleErrorSpy.mockRestore();
 * ```
 */
export const createMswHandler = (
  requestHandler: HttpRequestHandler,
  endpoint: string | RegExp,
  options?: IResponseOptions
): HttpHandler => {
  // Most uses of this function want a response to be returned, but don't care what it is. This
  // defaults the response to an empty object, while still allowing null to be used to indicate
  // there is no response, e.g. for a 204.
  const { statusCode = 200, response = {} } = options || {};
  return requestHandler(endpoint, () => {
    return new Response(response ? JSON.stringify(response) : null, {
      status: statusCode,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  });
};

/**
 * Creates a MSW GET handler for the given endpoint.
 */
export const createMswGetHandler = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): HttpHandler => {
  return createMswHandler(http.get, endpoint, options);
};

/**
 * Creates a MSW POST handler for the given endpoint.
 */
export const createMswPostHandler = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): HttpHandler => {
  return createMswHandler(http.post, endpoint, options);
};

/**
 * Creates a MSW PATCH handler for the given endpoint.
 */
export const createMswPatchHandler = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): HttpHandler => {
  return createMswHandler(http.patch, endpoint, options);
};

/**
 * Variation on `createMswHandler` that also returns functions that can be called to determine
 * if a request happened, and inspect what the body of the request was.
 */
const createMswHandlerSpy = (
  requestHandler: HttpRequestHandler,
  endpoint: string | RegExp,
  options?: IResponseOptions
): {
  getRequestQueryParams: () => URLSearchParams;
  getRequestJsonData: () => Record<string, unknown>;
  getWasCalled: () => boolean;
  handler: HttpHandler;
} => {
  const { statusCode = 200, response = {} } = options || {};

  let requestQueryParams: URLSearchParams;
  let requestJsonData: Record<string, unknown>;
  let wasCalled = false;

  const handler = requestHandler(endpoint, async ({ request }) => {
    requestQueryParams = new URL(request.url).searchParams;
    try {
      requestJsonData = (await request.json()) as Record<string, unknown>;
    } catch {
      // If the post request doesn't have a body, request.json() will throw an error
    } finally {
      wasCalled = true;
    }
    return new Response(response ? JSON.stringify(response) : null, {
      status: statusCode,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  });

  return {
    getRequestQueryParams: () => requestQueryParams,
    getRequestJsonData: () => requestJsonData,
    getWasCalled: () => wasCalled,
    handler,
  };
};

/**
 * Creates a MSW GET handler that allows inspecting query parameters.
 */
export const createMswGetHandlerSpy = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): {
  getRequestQueryParams: () => URLSearchParams;
  getWasCalled: () => boolean;
  handler: HttpHandler;
} => {
  const { getRequestQueryParams, getWasCalled, handler } = createMswHandlerSpy(
    http.get,
    endpoint,
    options
  );
  return {
    getRequestQueryParams,
    getWasCalled,
    handler,
  };
};

/**
 * Creates a MSW POST handler that allows inspecting the request body.
 */
export const createMswPostHandlerSpy = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): {
  getPostedValues: () => Record<string, unknown>;
  getWasCalled: () => boolean;
  handler: HttpHandler;
} => {
  const { getRequestJsonData, getWasCalled, handler } = createMswHandlerSpy(
    http.post,
    endpoint,
    options
  );
  return {
    getPostedValues: getRequestJsonData,
    getWasCalled,
    handler,
  };
};

/**
 * Creates a MSW PUT handler that allows inspecting the request body.
 */
export const createMswPutHandlerSpy = (
  endpoint: string | RegExp,
  options?: IResponseOptions
): {
  getPutValues: () => Record<string, unknown>;
  getWasCalled: () => boolean;
  handler: HttpHandler;
} => {
  const { getRequestJsonData, getWasCalled, handler } = createMswHandlerSpy(
    http.put,
    endpoint,
    options
  );
  return {
    getPutValues: getRequestJsonData,
    getWasCalled,
    handler,
  };
};
